import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Button, Grid, FormControl, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { Container, Content, ContentText, RadioGroupContainer } from "./styles";
import LoadingOverlay from "../../../../LoadingOverlay/LoadingOverlay";
import DialogTitle from "../../../../DialogTitle/DialogTitle";
import DialogActions from "../../../../DialogActions/DialogActions";

export enum ApproversOptions {
  exportSelectedDataWithActuallApprovers = "exportSelectedDataWithActuallApprovers",
  exportSelectedDataWithAllApprovers = "exportSelectedDataWithAllApprovers",
  exportAllDataWithActuallApprovers = "exportAllDataWithActuallApprovers",
  exportAllDataWithAllApprovers = "exportAllDataWithAllApprovers",
}

export const approversOptions: { [key: string]: { option: ApproversOptions; value: string } } = {
  exportSelectedDataWithActuallApprovers: {
    option: ApproversOptions.exportSelectedDataWithActuallApprovers,
    value: "Export as excel selected data with actuall approvers",
  },
  exportSelectedDataWithAllApprovers: {
    option: ApproversOptions.exportSelectedDataWithAllApprovers,
    value: "Export as excel selected data with all approvers",
  },
  exportAllDataWithActuallApprovers: {
    option: ApproversOptions.exportAllDataWithActuallApprovers,
    value: "Export as excel all data with actuall approvers",
  },
  exportAllDataWithAllApprovers: {
    option: ApproversOptions.exportAllDataWithAllApprovers,
    value: "Export as all selected data with all approvers",
  },
};

interface Props {
  handleExportToExcel: (option: ApproversOptions) => Promise<void>;
  disabled: boolean;
}

const ExportToExcelApproversDialog = ({ handleExportToExcel, disabled }: Props) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    approversOptions["exportSelectedDataWithActuallApprovers"].value
  );

  const handleExport = async () => {
    setLoading(true);
    try {
      const currOption = Object.keys(approversOptions).find((item) => approversOptions[item].value === selectedOption);

      if (!!currOption) {
        await handleExportToExcel(approversOptions[currOption].option);

        setLoading(false);

        setOpen(false);

        setSelectedOption(approversOptions["exportSelectedDataWithActuallApprovers"].value);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);

    setSelectedOption(approversOptions["exportSelectedDataWithActuallApprovers"].value);
  };

  return (
    <>
      <Button
        title={`${t(`common_words.export`)}`}
        variant="contained"
        onClick={() => setOpen(true)}
        sx={{ textWrap: "nowrap" }}
        disabled={disabled}
      >
        Export
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
      >
        <LoadingOverlay loading={loading}>
          <DialogTitle
            title={"Export as Excel"}
            handleClose={handleClose}
          />
          <Content>
            <Container>
              <Grid sx={{ padding: "5px" }}>
                <ContentText>Select how to export:</ContentText>
              </Grid>

              <RadioGroupContainer>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="radio-group"
                    defaultValue={approversOptions["exportSelectedDataWithActuallApprovers"].value}
                    name="radio-buttons-group"
                    onChange={(event, value) => {
                      setSelectedOption(value);
                    }}
                  >
                    {Object.keys(approversOptions).map((option) => {
                      return (
                        <FormControlLabel
                          key={approversOptions[option].value}
                          value={approversOptions[option].value}
                          control={<Radio />}
                          label={approversOptions[option].value}
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              </RadioGroupContainer>
            </Container>
          </Content>

          <DialogActions
            handleCancel={handleClose}
            useGoback
          >
            <Button
              onClick={handleExport}
              variant="contained"
            >
              {`${t(`buttons.confirm`)}`}
            </Button>
          </DialogActions>
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default ExportToExcelApproversDialog;
